import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { BANNER_TYPES } from '@sravni/react-advertisement';
import { useIsMobile } from '@sravni/react-utils';
import { Interceptor } from '@sravni/ugc/lib/utils/sentry/Interceptor';

import { SravniNewsSharedEnumsMaterialType, SravniNewsSharedEnumsPlacementType } from '../../__generated__/news/Api';
import { URL_TYPES } from '../../constants/news';
import { pushArticleViewEvent } from '../../helpers/analytics';
import { getMaterialLink } from '../../helpers/news';
import { useCurrentMaterialIndex, useMaterials, useRubricType, useTagType, useViewedArticles } from '../../hooks/news';
import { IMaterial, IRouterQuery, TRubricType } from '../../interfaces/news';
import { addViewedArticle, fetchOneMoreArticle, resetViewedArticle, setCurrentArticleIndex } from '../../reducers/news';
import { useWhitelabel } from '../../reducers/whitelabel';
import { formatForRequest } from '../../server/utils/formatForRequest';
import { increaseVisit } from '../../services/news';
import { isServer } from '../../utils/isServer';
import { removeExcludedRubrics } from '../../utils/news';
import { ReduxBanner } from '../ReduxBanner';
import { ScrollProgress } from '../ScrollProgress';

import { Article } from './components/Article';
import { ArticlePreloader } from './components/ArticlePreloader';
import { useCommentsObserver } from './helpers/useCommentsObserver';
import {
  TChangeUrl,
  TIncreaseVisit,
  toggleGetParamsEndlessScroll,
  useInfiniteWithVisiterAndChangeUrlScroll,
} from './helpers/useInfiniteScroll';

export const MaterialContent = () => {
  const {
    query: { type, rubricAlias },
  } = useRouter() as TRouter<IRouterQuery>;

  const [loading, setLoading] = useState(false);
  const materials = useMaterials();
  const currentArticleIndex = useCurrentMaterialIndex();

  const activeArticleAlias = materials?.[currentArticleIndex]?.alias;
  const currentArticle = useMemo(() => {
    return !isServer ? document.querySelector<HTMLDivElement>(`div[data-alias="${activeArticleAlias}"]`) : null;
  }, [activeArticleAlias]);

  const viewedArticles = useViewedArticles();

  const dispatch = useDispatch();

  const isNews = type === URL_TYPES[SravniNewsSharedEnumsMaterialType.News];

  const isWhitelabel = useWhitelabel();
  const isMobile = useIsMobile();

  let rubricType = useRubricType();
  const tagType = useTagType();

  const materialsRef = useRef(materials);

  const firstArticleRubricType = materialsRef.current[0]?.rubric as TRubricType;
  const isPartnerNews = [rubricAlias, firstArticleRubricType].some((item) => item === 'novosti-partnerov');

  if (isPartnerNews) {
    rubricType = 'novosti-partnerov';
  }

  const loadMore = async () => {
    setLoading(true);

    const organizationId = materialsRef.current[0]?.organizationData?.id;
    const rubrics = removeExcludedRubrics([rubricType || firstArticleRubricType]);

    await dispatch(
      fetchOneMoreArticle({
        DateFrom: formatForRequest(new Date()),
        Take: 1,
        Skip: 0,
        type: isNews ? SravniNewsSharedEnumsMaterialType.News : SravniNewsSharedEnumsMaterialType.Article,
        excludeExternals: true,
        excludeExternalsFromPlacements: !isMobile,
        Placement: SravniNewsSharedEnumsPlacementType.EndlessScroll,
        isPartnerNews,
        ...(rubricAlias && rubrics && { Rubrics: rubrics }),
        ...(tagType && { Tags: [tagType] }),
        ...(isPartnerNews && { organizationId }),
      }),
    );
    setLoading(false);
  };

  const handleIncreaseVisit: TIncreaseVisit = async (material: IMaterial) => {
    if (material) {
      const isFirst = materials[0].id === material.id;
      !isFirst && pushArticleViewEvent(material, window.location.pathname);
      isFirst && changeUrlByMaterial(material);
      await increaseVisit(material.id);
      dispatch(addViewedArticle(material.id));
    }
  };

  const changeUrlByMaterial: TChangeUrl = (material: IMaterial) => {
    const { title } = material;

    document.title = title.replace(/<img[^>]+\>/i, '');

    dispatch(setCurrentArticleIndex(material.alias));

    toggleGetParamsEndlessScroll(material.alias);

    const { as } = getMaterialLink(material);
    const url = as.split('?')[0];
    window.history.pushState({ alias: material.alias }, null, `${url}${window.location.search}`);
  };

  const observer = useInfiniteWithVisiterAndChangeUrlScroll({
    loadMore,
    increaseVisit: handleIncreaseVisit,
    changeUrl: changeUrlByMaterial,
  });

  const commentsObserver = useCommentsObserver();

  const scrollToArticleByAlias = (alias: string) => {
    document.getElementById(alias)?.scrollIntoView({ behavior: 'smooth' });
    dispatch(setCurrentArticleIndex(alias));
  };

  useEffect(() => {
    dispatch(resetViewedArticle());
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    window.history.scrollRestoration = 'manual';
    const handlePopState = (event: PopStateEvent) => {
      if (event) {
        scrollToArticleByAlias(event.state?.alias);
      }
    };
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.history.scrollRestoration = 'auto';
      window.removeEventListener('popstate', handlePopState);
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  return (
    <>
      <ScrollProgress container={currentArticle} />
      {materials?.map((material, index) => {
        const isShowArticle = [index - 1, index, index + 1].includes(currentArticleIndex);
        const canSpecialOffersBeLoaded = viewedArticles.includes(material.id);
        const noIndex = material.rubric === 'specproekty';

        const item = (
          <Interceptor componentName="news-material-component-Article" key={material.id}>
            <div data-js={'articles-list'} className={'h-mb-32'}>
              <Article
                index={index}
                isShowArticle={isShowArticle}
                observer={observer}
                commentsObserver={commentsObserver}
                material={material}
                isUserReadingThisArticle={activeArticleAlias === material.alias}
                titleTag={index === 0 ? 'h1' : 'h2'}
                canSpecialOffersBeLoaded={canSpecialOffersBeLoaded}
              />
            </div>
          </Interceptor>
        );

        if (noIndex) {
          return <noindex key={material.id}>{item}</noindex>;
        }

        return item;
      })}
      <div
        style={{ display: loading ? 'block' : 'none', ...(!isMobile && { paddingRight: '316px' }) }}
        className={'h-mt-24 h-mb-24'}
      >
        <ArticlePreloader />
      </div>
      {!isWhitelabel && (
        <>
          {/*<Interceptor componentName="news-material-component-SeoBlocks">/!*<SeoBlocks />*!/</Interceptor>*/}
          <ReduxBanner type={BANNER_TYPES.OUT_OF_PAGE} />
        </>
      )}
    </>
  );
};
